import createClient, {FetchResponse, Middleware} from "openapi-fetch";
import type { paths } from "@gen/openapi";
import {buildUrl, MaybePromise} from "./utils";

// Consumers concatenate paths as `${API_BASE_URL}v1/api/...`, so the value
// must end with `/`. Tenant .env files (e.g. vergersleahy) sometimes leave the
// slash off, which used to produce URLs like `https://…balancia.cloudv1/…`
// and broke the pdfme section editor + every unnormalised call site
// (useStorage, PushNotificationTest, products/create, storage index, …).
const _rawApiBase = import.meta.env.VITE_API_URL || "http://localhost:3210/";
const API_BASE_URL = _rawApiBase.endsWith("/") ? _rawApiBase : `${_rawApiBase}/`;

const api = createClient<paths>({
    baseUrl: API_BASE_URL,
});

export const warpApiResponse = <T extends FetchResponse<any, any, any>>(r: T) => {
    const { error, response } = r;
    if (!response.ok) {
        throw buildApiError(error, response);
    }
    return r;
}

export class ApiRequestError extends Error {
    readonly status: number;
    readonly messages?: Record<string, string[]>;

    constructor(args: {status: number; message: string; messages?: Record<string, string[]>}) {
        super(args.message);
        this.name = 'ApiRequestError';
        this.status = args.status;
        this.messages = args.messages;
    }
}

const buildApiError = (error: unknown, response: Response) => {
    const payload = error && typeof error === 'object'
        ? error as {message?: string; messages?: Record<string, string[]>}
        : {};

    return new ApiRequestError({
        status: response.status,
        message: payload.message ?? response.statusText ?? 'Unknown API error',
        ...(payload.messages ? {messages: payload.messages} : {}),
    });
};

export const warpApiMutation = async <T extends MaybePromise<FetchResponse<any, any, any>>>(r: T) => {
    const res = await r;

    if (!res.response.ok) {
        throw buildApiError(res.error, res.response);
    }

    return res.data
}

// ---------------------------------------------------------------------------
// Token refresh coordination
// ---------------------------------------------------------------------------

let onForcedLogoutCallback: (() => void) | null = null;

/**
 * Register a callback invoked when refresh fails and the user must sign in again.
 * Returns an unregister function. The api module wipes tokens before calling the
 * callback, so the callback only needs to clear in-memory state and navigate.
 */
export const registerOnForcedLogout = (cb: () => void) => {
    onForcedLogoutCallback = cb;
    return () => {
        if (onForcedLogoutCallback === cb) onForcedLogoutCallback = null;
    };
};

let inFlightRefresh: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return null;

    try {
        const refreshUrl: keyof paths = '/v1/api/auth/refresh';
        const res = await fetch(buildUrl(API_BASE_URL, refreshUrl), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return null;
        const data = await res.json();
        if (!data?.accessToken) return null;
        localStorage.setItem("token", data.accessToken);
        if (data.refreshToken) localStorage.setItem("refreshToken", data.refreshToken);
        return data.accessToken;
    } catch {
        return null;
    }
}

/**
 * Run a refresh attempt under a cross-tab lock so that, when several tabs all
 * notice an expired token at once, only one performs the network call and the
 * rest pick up the resulting token. Falls back to in-tab dedup if Web Locks
 * are unavailable.
 *
 * `usedToken` is the access token that produced the 401. When we eventually
 * acquire the lock, if the current localStorage token differs we assume another
 * tab refreshed for us and return that token without a network call.
 */
async function refreshAccessToken(usedToken: string | null): Promise<string | null> {
    const tryRefresh = async (): Promise<string | null> => {
        const current = localStorage.getItem("token");
        if (current && current !== usedToken) return current;
        return performRefresh();
    };

    if (typeof navigator !== "undefined" && navigator.locks) {
        return navigator.locks.request("balancia-auth-refresh", tryRefresh);
    }

    if (!inFlightRefresh) {
        inFlightRefresh = tryRefresh().finally(() => { inFlightRefresh = null; });
    }
    return inFlightRefresh;
}

function forceLogout() {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    if (import.meta.env.DEV) {
        console.warn("[auth] Forced re-login: refresh token expired or invalid");
    }
    onForcedLogoutCallback?.();
}

// ---------------------------------------------------------------------------
// Middleware
// ---------------------------------------------------------------------------

// Capture original request data before fetch consumes the body, keyed by
// openapi-fetch's per-request id. Cleaned up in both onResponse and onError.
type CapturedRequest = {
    url: string;
    method: string;
    headers: Headers;
    body: string | null;
};
const captured = new Map<string, CapturedRequest>();

const onRequest: Middleware['onRequest'] = async ({ request, id }) => {
    const token = localStorage.getItem("token");
    if (token) {
        request.headers.set("Authorization", `Bearer ${token}`);
    }

    let body: string | null = null;
    if (request.method !== "GET" && request.method !== "HEAD") {
        try {
            body = await request.clone().text();
        } catch {
            body = null;
        }
    }
    captured.set(id, {
        url: request.url,
        method: request.method,
        headers: new Headers(request.headers),
        body,
    });
    return request;
};

const onResponse: Middleware['onResponse'] = async ({ response, request, id }) => {
    const original = captured.get(id);
    captured.delete(id);

    if (response.status !== 401) return response;
    if (request.url.includes("/auth/refresh")) return response;

    const usedToken = original?.headers.get("Authorization")?.replace(/^Bearer\s+/i, "") ?? null;
    const newToken = await refreshAccessToken(usedToken);
    if (!newToken) {
        forceLogout();
        return response;
    }

    // Build a fresh Request from the captured data — the original request's body
    // stream has already been consumed by fetch, so cloning it would throw for
    // POST/PUT/PATCH. Reuse the captured headers + body to retry safely.
    const retryHeaders = new Headers(original?.headers ?? request.headers);
    retryHeaders.set("Authorization", `Bearer ${newToken}`);

    return fetch(original?.url ?? request.url, {
        method: original?.method ?? request.method,
        headers: retryHeaders,
        body: original?.body ?? null,
    });
};

const onError: Middleware['onError'] = async ({ id }) => {
    captured.delete(id);
    return undefined;
};

const authMiddleware: Middleware = {
    onRequest,
    onResponse,
    onError,
};

api.use(authMiddleware);

export default api;
export { API_BASE_URL };


export const validateApiUrl = <S extends keyof paths>(url: S) => {
    return url;
}
